import React, { useState, useEffect, useCallback, Component } from 'react';
import {
  FolderTree, Plus, Trash2, Loader2, ChevronRight, ChevronDown,
  Users, DollarSign, Cpu, AlertTriangle, Download, Edit2, X, Check,
  UserPlus, UserMinus, Shield
} from 'lucide-react';
import { API } from '../../App';

const SUB_TABS = [
  { id: 'hierarchy', label: 'Hierarchy', icon: FolderTree },
  { id: 'budgets', label: 'Budgets & Costs', icon: DollarSign },
  { id: 'policies', label: 'Model Policies', icon: Cpu },
];

/* ── Shared UI ── */
const Card = ({ children, className = '' }) => (
  <div className={`bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 ${className}`}>{children}</div>
);
const Input = (props) => (
  <input {...props} className={`bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none w-full ${props.className || ''}`} />
);
const Btn = ({ children, variant = 'primary', className = '', ...props }) => {
  const base = variant === 'primary'
    ? 'bg-[#FF4D00] text-[#0A0A0A] font-bold hover:bg-[#E63E00]'
    : variant === 'danger'
      ? 'bg-[#A61E17] text-[#F5F1E8] font-bold hover:bg-[#8B1912]'
      : 'bg-[#2A2A2A] text-[#F5F1E8] hover:bg-[#333]';
  return (
    <button {...props} className={`${base} text-sm rounded px-3 py-2 disabled:opacity-40 flex items-center gap-1.5 min-h-[36px] ${className}`}>
      {children}
    </button>
  );
};
const Spinner = () => <Loader2 className="w-4 h-4 animate-spin text-[#A0A0A0]" />;
const Err = ({ msg }) => msg ? <p className="text-[#A61E17] text-xs mt-1">{msg}</p> : null;

/* ══════════════════════════════════════════
   HIERARCHY TAB
   ══════════════════════════════════════════ */

function DeptNode({ dept, level, selected, onSelect, onToggle, expanded }) {
  const hasChildren = dept.children && dept.children.length > 0;
  const isOpen = expanded[dept.id];

  return (
    <>
      <div
        onClick={() => onSelect(dept)}
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded text-sm transition-colors ${
          selected?.id === dept.id ? 'bg-[#FF4D00]/10 border border-[#FF4D00]/30' : 'hover:bg-[#2A2A2A]'
        }`}
        style={{ marginLeft: level * 24 }}
      >
        {level > 0 && <div className="border-l border-[#2A2A2A] h-full absolute" style={{ left: level * 24 - 12 }} />}
        {hasChildren ? (
          <button onClick={(e) => { e.stopPropagation(); onToggle(dept.id); }} className="p-0.5">
            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-[#A0A0A0]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#A0A0A0]" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <FolderTree className="w-3.5 h-3.5 text-[#FF4D00]" />
        <span className="font-mono text-[#F5F1E8]">{dept.name}</span>
        {dept.member_count != null && (
          <span className="text-[10px] text-[#A0A0A0] ml-auto font-mono">{dept.member_count} members</span>
        )}
      </div>
      {hasChildren && isOpen && dept.children.map(child => (
        <DeptNode key={child.id} dept={child} level={level + 1} selected={selected} onSelect={onSelect} onToggle={onToggle} expanded={expanded} />
      ))}
    </>
  );
}

function HierarchyTab({ workspaceId }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newParent, setNewParent] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // Members
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [wsMembers, setWsMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const loadDepartments = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [deptRes, hierRes] = await Promise.all([
        API.get('/api/enterprise/departments', { params: { workspace_id: workspaceId } }),
        API.get('/api/enterprise/departments/hierarchy', { params: { workspace_id: workspaceId } }).catch(() => ({ data: [] })),
      ]);
      const depts = deptRes.data?.departments ?? deptRes.data ?? [];
      setDepartments(Array.isArray(depts) ? depts : []);
      const hier = hierRes.data?.hierarchy ?? hierRes.data ?? [];
      setHierarchy(Array.isArray(hier) ? hier : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load departments');
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { loadDepartments(); }, [loadDepartments]);

  // Fetch workspace members for autocomplete (#533)
  useEffect(() => {
    if (!workspaceId) return;
    API.get(`/api/workspaces/${workspaceId}`).then(r => {
      setWsMembers((r.data.members || []).filter(m => m.status !== 'invited'));
    }).catch(() => {});
  }, [workspaceId]);

  const filteredSuggestions = memberSearch.length > 0
    ? wsMembers.filter(m =>
        (m.email || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
        (m.name || '').toLowerCase().includes(memberSearch.toLowerCase())
      ).filter(m => !members.some(dm => dm.user_id === m.id))
    : [];

  const loadMembers = useCallback(async (deptId) => {
    setMembersLoading(true);
    try {
      const r = await API.get(`/api/enterprise/departments/${deptId}/members`);
      const mList = r.data?.members ?? r.data ?? [];
      setMembers(Array.isArray(mList) ? mList : []);
    } catch { setMembers([]); }
    setMembersLoading(false);
  }, []);

  useEffect(() => {
    if (selected) loadMembers(selected.id);
    else setMembers([]);
  }, [selected, loadMembers]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await API.post('/api/enterprise/departments', {
        workspace_id: workspaceId,
        name: newName.trim(),
        parent_id: newParent || null,
        description: newDesc.trim() || null,
      });
      setNewName(''); setNewDesc(''); setNewParent(''); setShowCreate(false);
      loadDepartments();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create');
    }
    setCreating(false);
  };

  const handleUpdate = async () => {
    if (!selected || !editName.trim()) return;
    setError('');
    try {
      await API.put(`/api/enterprise/departments/${selected.id}`, {
        name: editName.trim(),
        description: editDesc.trim() || null,
      });
      setEditing(false);
      setSelected(null);
      loadDepartments();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (deptId) => {
    setError('');
    try {
      await API.delete(`/api/enterprise/departments/${deptId}`);
      setSelected(null);
      loadDepartments();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddMember = async () => {
    if (!selected || !addUserId.trim()) return;
    setError('');
    try {
      await API.post(`/api/enterprise/departments/${selected.id}/members`, {
        user_id: addUserId.trim(),
        role: addRole,
      });
      setAddUserId('');
      loadMembers(selected.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selected) return;
    try {
      await API.delete(`/api/enterprise/departments/${selected.id}/members/${userId}`);
      loadMembers(selected.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Spinner /> Loading departments...</div>;

  const treeData = hierarchy.length > 0 ? hierarchy : departments.map(d => ({ ...d, children: [] }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-['Barlow_Condensed'] font-bold text-[#F5F1E8] uppercase tracking-wider">Department Hierarchy</h3>
        <Btn onClick={() => setShowCreate(!showCreate)}><Plus className="w-3.5 h-3.5" /> New Department</Btn>
      </div>

      <Err msg={error} />

      {showCreate && (
        <Card>
          <div className="space-y-3">
            <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider">Create Department</p>
            <Input placeholder="Department name" value={newName} onChange={e => setNewName(e.target.value)} />
            <Input placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
            <select
              value={newParent}
              onChange={e => setNewParent(e.target.value)}
              className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none w-full"
            >
              <option value="">No parent (root)</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div className="flex gap-2">
              <Btn onClick={handleCreate} disabled={creating || !newName.trim()}>
                {creating ? <Spinner /> : <Check className="w-3.5 h-3.5" />} Create
              </Btn>
              <Btn variant="ghost" onClick={() => setShowCreate(false)}><X className="w-3.5 h-3.5" /> Cancel</Btn>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tree */}
        <Card>
          <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider mb-3">Departments</p>
          {treeData.length === 0 ? (
            <p className="text-sm text-[#A0A0A0] py-4 text-center">No departments yet. Create one to get started.</p>
          ) : (
            <div className="space-y-0.5 relative">
              {treeData.map(dept => (
                <DeptNode key={dept.id} dept={dept} level={0} selected={selected} onSelect={setSelected} onToggle={toggleExpand} expanded={expanded} />
              ))}
            </div>
          )}
        </Card>

        {/* Detail panel */}
        <Card>
          {selected ? (
            <div className="space-y-4">
              {editing ? (
                <div className="space-y-3">
                  <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" />
                  <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" />
                  <div className="flex gap-2">
                    <Btn onClick={handleUpdate}><Check className="w-3.5 h-3.5" /> Save</Btn>
                    <Btn variant="ghost" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /> Cancel</Btn>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-base font-['Barlow_Condensed'] font-bold text-[#F5F1E8]">{selected.name}</h4>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditing(true); setEditName(selected.name); setEditDesc(selected.description || ''); }}
                        className="p-1.5 hover:bg-[#2A2A2A] rounded"><Edit2 className="w-3.5 h-3.5 text-[#A0A0A0]" /></button>
                      <button onClick={() => handleDelete(selected.id)}
                        className="p-1.5 hover:bg-[#A61E17]/20 rounded"><Trash2 className="w-3.5 h-3.5 text-[#A61E17]" /></button>
                    </div>
                  </div>
                  {selected.description && <p className="text-xs text-[#A0A0A0] mb-3">{selected.description}</p>}
                </div>
              )}

              {/* Members */}
              <div>
                <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Users className="w-3 h-3" /> Members
                </p>
                {membersLoading ? <Spinner /> : (
                  <>
                    {members.length === 0 ? (
                      <p className="text-xs text-[#A0A0A0]">No members assigned.</p>
                    ) : (
                      <div className="space-y-1 mb-3">
                        {members.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between py-1.5 px-2 bg-[#0F0F0F] rounded text-xs">
                            <div className="flex items-center gap-2">
                              <Users className="w-3 h-3 text-[#00D4FF]" />
                              <span className="font-mono text-[#F5F1E8]">{m.email || m.user_id}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2A2A2A] text-[#A0A0A0]">{m.role}</span>
                            </div>
                            <button onClick={() => handleRemoveMember(m.user_id)} className="p-1 hover:bg-[#A61E17]/20 rounded">
                              <UserMinus className="w-3 h-3 text-[#A61E17]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2 relative">
                      <div className="flex-1 relative">
                        <Input
                          placeholder="Search by email or name..."
                          value={memberSearch}
                          onChange={e => { setMemberSearch(e.target.value); setShowSuggestions(true); }}
                          onFocus={() => setShowSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        />
                        {showSuggestions && filteredSuggestions.length > 0 && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg max-h-40 overflow-y-auto shadow-lg">
                            {filteredSuggestions.map(m => (
                              <button
                                key={m.id}
                                onMouseDown={(e) => { e.preventDefault(); setAddUserId(m.id); setMemberSearch(m.email || m.name); setShowSuggestions(false); }}
                                className="w-full text-left px-3 py-2 text-sm font-mono hover:bg-[#FF4D00]/10 transition-colors flex items-center gap-2"
                              >
                                <span className="text-[#F5F1E8]">{m.email}</span>
                                {m.name && <span className="text-[#A0A0A0] text-xs">({m.name})</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <select value={addRole} onChange={e => setAddRole(e.target.value)}
                        className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none">
                        <option value="member">Member</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                      <Btn onClick={() => { handleAddMember(); setMemberSearch(''); }} disabled={!addUserId.trim()}><UserPlus className="w-3.5 h-3.5" /> Add</Btn>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-[#A0A0A0]">
              <FolderTree className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">Select a department to view details</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   BUDGETS & COSTS TAB
   ══════════════════════════════════════════ */

function BudgetsCostsTab({ workspaceId }) {
  const [departments, setDepartments] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [costs, setCosts] = useState(null);
  const [deptCosts, setDeptCosts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Budget form
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetDeptId, setBudgetDeptId] = useState('');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [budgetThreshold, setBudgetThreshold] = useState('80');
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState('current_month');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [deptRes, budgetRes, costRes, deptCostRes, alertRes] = await Promise.all([
        API.get('/api/enterprise/departments', { params: { workspace_id: workspaceId } }),
        API.get('/api/enterprise/budgets', { params: { workspace_id: workspaceId } }),
        API.get('/api/enterprise/costs', { params: { workspace_id: workspaceId, period } }).catch(() => ({ data: null })),
        API.get('/api/enterprise/costs/by-department', { params: { workspace_id: workspaceId, period } }).catch(() => ({ data: [] })),
        API.get('/api/enterprise/budgets/alerts', { params: { workspace_id: workspaceId } }).catch(() => ({ data: [] })),
      ]);
      const depts = deptRes.data?.departments ?? deptRes.data ?? [];
      setDepartments(Array.isArray(depts) ? depts : []);
      const bList = budgetRes.data?.budgets ?? budgetRes.data ?? [];
      setBudgets(Array.isArray(bList) ? bList : []);
      setCosts(budgetRes.data?.summary ?? costRes.data?.summary ?? costRes.data);
      const dcList = deptCostRes.data?.costs ?? deptCostRes.data ?? [];
      setDeptCosts(Array.isArray(dcList) ? dcList : []);
      const aList = alertRes.data?.alerts ?? alertRes.data ?? [];
      setAlerts(Array.isArray(aList) ? aList : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load budget data');
    }
    setLoading(false);
  }, [workspaceId, period]);

  useEffect(() => { load(); }, [load]);

  const handleSaveBudget = async () => {
    if (!budgetDeptId || !budgetLimit) return;
    setSaving(true);
    setError('');
    try {
      await API.post('/api/enterprise/budgets', {
        workspace_id: workspaceId,
        department_id: budgetDeptId,
        monthly_limit: parseFloat(budgetLimit),
        alert_threshold: parseFloat(budgetThreshold) / 100,
      });
      setShowBudgetForm(false);
      setBudgetDeptId(''); setBudgetLimit(''); setBudgetThreshold('80');
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save budget');
    }
    setSaving(false);
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      await API.delete(`/api/enterprise/budgets/${budgetId}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const r = await API.get('/api/enterprise/costs/export', {
        params: { workspace_id: workspaceId },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `costs-${workspaceId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export');
    }
  };

  const deptMap = Object.fromEntries(departments.map(d => [d.id, d.name]));

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Spinner /> Loading budgets...</div>;

  return (
    <div className="space-y-4">
      {/* Alerts banner */}
      {alerts.length > 0 && (
        <Card className="border-[#A61E17]/50 bg-[#A61E17]/5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[#F2A900]" />
            <span className="text-sm font-bold text-[#F2A900]">Budget Alerts</span>
          </div>
          <div className="space-y-1">
            {alerts.map((a, i) => (
              <p key={i} className="text-xs text-[#F5F1E8] font-mono">
                <span className="text-[#FF4D00]">{deptMap[a.department_id] || a.department_id}</span>
                {' — '}{a.message || `${Math.round((a.usage_pct || 0) * 100)}% of budget used`}
              </p>
            ))}
          </div>
        </Card>
      )}

      {/* Cost summary */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-['Barlow_Condensed'] font-bold text-[#F5F1E8] uppercase tracking-wider">Cost Overview</h3>
        <div className="flex gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none">
            <option value="current_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>
          <Btn variant="ghost" onClick={handleExport}><Download className="w-3.5 h-3.5" /> Export CSV</Btn>
        </div>
      </div>

      <Err msg={error} />

      {costs && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Cost', value: `$${(costs.total_cost || 0).toFixed(2)}`, color: '#F5F1E8' },
            { label: 'Total Debates', value: costs.total_debates || 0, color: '#00D4FF' },
            { label: 'Avg/Debate', value: `$${(costs.avg_cost_per_debate || 0).toFixed(3)}`, color: '#F2A900' },
            { label: 'Departments', value: departments.length, color: '#FF4D00' },
          ].map(s => (
            <Card key={s.label}>
              <p className="text-[10px] font-mono text-[#A0A0A0] uppercase">{s.label}</p>
              <p className="text-lg font-bold font-mono mt-1" style={{ color: s.color }}>{s.value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Per-department costs */}
      {deptCosts.length > 0 && (
        <Card>
          <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider mb-3">Cost by Department</p>
          <div className="space-y-2">
            {deptCosts.map(dc => {
              const budget = budgets.find(b => b.department_id === dc.department_id);
              const pct = budget ? (dc.total_cost / budget.monthly_limit) * 100 : 0;
              return (
                <div key={dc.department_id} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono text-[#F5F1E8]">{deptMap[dc.department_id] || dc.department_id}</span>
                    <span className="font-mono text-[#00D4FF]">${(dc.total_cost || 0).toFixed(2)}</span>
                  </div>
                  {budget && (
                    <div className="w-full bg-[#0F0F0F] rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: `${Math.min(pct, 100)}%`,
                          backgroundColor: pct > 90 ? '#A61E17' : pct > 70 ? '#F2A900' : '#00D4FF',
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Budgets */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-['Barlow_Condensed'] font-bold text-[#F5F1E8] uppercase tracking-wider">Department Budgets</h3>
        <Btn onClick={() => setShowBudgetForm(!showBudgetForm)}><Plus className="w-3.5 h-3.5" /> Set Budget</Btn>
      </div>

      {showBudgetForm && (
        <Card>
          <div className="space-y-3">
            <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider">Configure Budget</p>
            <select value={budgetDeptId} onChange={e => setBudgetDeptId(e.target.value)}
              className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none w-full">
              <option value="">Select department</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Monthly Limit ($)</label>
                <Input type="number" placeholder="500.00" value={budgetLimit} onChange={e => setBudgetLimit(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Alert at (%)</label>
                <Input type="number" placeholder="80" value={budgetThreshold} onChange={e => setBudgetThreshold(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2">
              <Btn onClick={handleSaveBudget} disabled={saving || !budgetDeptId || !budgetLimit}>
                {saving ? <Spinner /> : <Check className="w-3.5 h-3.5" />} Save
              </Btn>
              <Btn variant="ghost" onClick={() => setShowBudgetForm(false)}><X className="w-3.5 h-3.5" /> Cancel</Btn>
            </div>
          </div>
        </Card>
      )}

      {budgets.length === 0 ? (
        <p className="text-sm text-[#A0A0A0] text-center py-4">No budgets configured yet.</p>
      ) : (
        <div className="space-y-2">
          {budgets.map(b => (
            <Card key={b.id} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-mono text-[#F5F1E8]">{deptMap[b.department_id] || b.department_id}</p>
                <p className="text-xs text-[#A0A0A0] font-mono">
                  Limit: <span className="text-[#00D4FF]">${(b.monthly_limit || 0).toFixed(2)}</span>
                  {' / '}Alert at: <span className="text-[#F2A900]">{Math.round((b.alert_threshold || 0.8) * 100)}%</span>
                </p>
              </div>
              <button onClick={() => handleDeleteBudget(b.id)} className="p-1.5 hover:bg-[#A61E17]/20 rounded">
                <Trash2 className="w-3.5 h-3.5 text-[#A61E17]" />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MODEL POLICIES TAB
   ══════════════════════════════════════════ */

function ModelPoliciesTab({ workspaceId }) {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Policy form
  const [allowedModels, setAllowedModels] = useState('');
  const [costLimit, setCostLimit] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [restrictions, setRestrictions] = useState('');

  const loadDepts = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await API.get('/api/enterprise/departments', { params: { workspace_id: workspaceId } });
      const depts = r.data?.departments ?? r.data ?? [];
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load');
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { loadDepts(); }, [loadDepts]);

  const loadPolicy = useCallback(async (deptId) => {
    setPolicyLoading(true);
    setPolicy(null);
    try {
      const r = await API.get(`/api/enterprise/departments/${deptId}/model-policy`);
      const p = r.data?.policy ?? r.data ?? null;
      setPolicy(p);
      if (p) {
        setAllowedModels((p.allowed_models || []).join(', '));
        setCostLimit(p.cost_limit_per_debate?.toString() || p.daily_budget_usd?.toString() || '');
        setMaxTokens(p.max_tokens?.toString() || p.max_tokens_per_request?.toString() || '');
        setRestrictions(p.restrictions || '');
      } else {
        setAllowedModels(''); setCostLimit(''); setMaxTokens(''); setRestrictions('');
      }
    } catch {
      setPolicy(null);
      setAllowedModels(''); setCostLimit(''); setMaxTokens(''); setRestrictions('');
    }
    setPolicyLoading(false);
  }, []);

  useEffect(() => {
    if (selectedDept) loadPolicy(selectedDept.id);
  }, [selectedDept, loadPolicy]);

  const handleSavePolicy = async () => {
    if (!selectedDept) return;
    setSaving(true);
    setError('');
    try {
      await API.put(`/api/enterprise/departments/${selectedDept.id}/model-policy`, {
        allowed_models: allowedModels.split(',').map(s => s.trim()).filter(Boolean),
        cost_limit_per_debate: costLimit ? parseFloat(costLimit) : null,
        max_tokens: maxTokens ? parseInt(maxTokens) : null,
        restrictions: restrictions.trim() || null,
      });
      loadPolicy(selectedDept.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save policy');
    }
    setSaving(false);
  };

  const handleDeletePolicy = async () => {
    if (!selectedDept) return;
    try {
      await API.delete(`/api/enterprise/departments/${selectedDept.id}/model-policy`);
      setPolicy(null);
      setAllowedModels(''); setCostLimit(''); setMaxTokens(''); setRestrictions('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete policy');
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Spinner /> Loading...</div>;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-['Barlow_Condensed'] font-bold text-[#F5F1E8] uppercase tracking-wider">Per-Department Model Policies</h3>

      <Err msg={error} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Department list */}
        <Card>
          <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider mb-3">Select Department</p>
          {departments.length === 0 ? (
            <p className="text-sm text-[#A0A0A0] text-center py-4">No departments. Create departments in the Hierarchy tab first.</p>
          ) : (
            <div className="space-y-1">
              {departments.map(d => (
                <div
                  key={d.id}
                  onClick={() => setSelectedDept(d)}
                  className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${
                    selectedDept?.id === d.id ? 'bg-[#FF4D00]/10 border border-[#FF4D00]/30' : 'hover:bg-[#2A2A2A]'
                  }`}
                >
                  <Shield className="w-3.5 h-3.5 text-[#00D4FF]" />
                  <span className="font-mono text-[#F5F1E8]">{d.name}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Policy editor */}
        <Card>
          {selectedDept ? (
            policyLoading ? <div className="py-8 flex justify-center"><Spinner /></div> : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono text-[#A0A0A0] uppercase tracking-wider">
                    Policy: <span className="text-[#FF4D00]">{selectedDept.name}</span>
                  </p>
                  {policy && (
                    <button onClick={handleDeletePolicy} className="p-1.5 hover:bg-[#A61E17]/20 rounded">
                      <Trash2 className="w-3.5 h-3.5 text-[#A61E17]" />
                    </button>
                  )}
                </div>

                <div>
                  <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Allowed Models (comma-separated)</label>
                  <Input placeholder="gpt-4o, claude-sonnet-4-20250514, gemini-pro" value={allowedModels} onChange={e => setAllowedModels(e.target.value)} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Cost Limit / Debate ($)</label>
                    <Input type="number" placeholder="2.00" value={costLimit} onChange={e => setCostLimit(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Max Tokens</label>
                    <Input type="number" placeholder="4096" value={maxTokens} onChange={e => setMaxTokens(e.target.value)} />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-mono text-[#A0A0A0] uppercase mb-1 block">Additional Restrictions</label>
                  <textarea
                    value={restrictions}
                    onChange={e => setRestrictions(e.target.value)}
                    placeholder="e.g. No image generation models"
                    rows={3}
                    className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none w-full resize-none"
                  />
                </div>

                <Btn onClick={handleSavePolicy} disabled={saving}>
                  {saving ? <Spinner /> : <Check className="w-3.5 h-3.5" />} Save Policy
                </Btn>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-[#A0A0A0]">
              <Cpu className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">Select a department to configure its model policy</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════ */

class DepartmentErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-[#A0A0A0]">
          <AlertTriangle className="w-8 h-8 mb-3 text-[#F2A900]" />
          <p className="text-sm font-['Barlow_Condensed'] font-bold text-[#F5F1E8] mb-1">Something went wrong</p>
          <p className="text-xs text-[#A0A0A0] mb-4">The Departments panel encountered an error.</p>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            className="bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded px-4 py-2 hover:bg-[#E63E00]">
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DepartmentSection({ workspaceId }) {
  const [subTab, setSubTab] = useState('hierarchy');

  return (
    <DepartmentErrorBoundary>
      <div className="space-y-4">
        {/* Sub-tab bar */}
        <div className="flex gap-1 border-b border-[#2A2A2A] pb-3">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setSubTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-t transition-colors ${
                  subTab === t.id
                    ? 'bg-[#FF4D00] text-[#0A0A0A] font-bold'
                    : 'text-[#A0A0A0] hover:text-[#F5F1E8] hover:bg-[#2A2A2A]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {subTab === 'hierarchy' && <HierarchyTab workspaceId={workspaceId} />}
        {subTab === 'budgets' && <BudgetsCostsTab workspaceId={workspaceId} />}
        {subTab === 'policies' && <ModelPoliciesTab workspaceId={workspaceId} />}
      </div>
    </DepartmentErrorBoundary>
  );
}
